import {
  Card,
  DiffViewerContentCols,
  DiffViewerTitleCols,
  Row,
} from '@/components';
import {
  formatOwnershipRatioFraction,
  formatOwnershipRatioPartsOfFraction,
  formatOwnershipRatioPercentage,
} from '@/features/mold/utils';
import { isBefore2025 } from '@/features/project/_utils/project';
import type { DiffViewConfigType } from '@/types/diff-view-config';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  ExistenceOrAbsenceLabelModifier,
  type InspectionEvaluationKsModel,
} from '../../../_models/project';

type InspectionEvaluation = z.infer<typeof InspectionEvaluationKsModel>;
type PartialInspectionEvaluation = Partial<InspectionEvaluation>;
type OwnershipRatio = InspectionEvaluation['ownershipRatio'];

type Props = {
  sourceFiscalYear: number;
  targetFiscalYear: number;
  sourceInspectionEvaluation: PartialInspectionEvaluation; // left side
  targetInspectionEvaluation: PartialInspectionEvaluation; // right side
};

export const KsInspectionEvaluationDiffView: FC<Props> = ({
  sourceFiscalYear,
  targetFiscalYear,
  sourceInspectionEvaluation,
  targetInspectionEvaluation,
}) => {
  const { t } = useTranslation();
  const isBefore2025Source = isBefore2025({ fiscalYear: sourceFiscalYear });
  const isBefore2025Target = isBefore2025({ fiscalYear: targetFiscalYear });

  const selfInspectionByFundJurisdictionLegendMap: Array<
    DiffViewConfigType<OwnershipRatio>
  > = [
    {
      prop: 'operationType',
      label: t('ownership-ratio'),
      converter: (_, ownershipRatio) =>
        ownershipRatio?.operationType === 'auto'
          ? formatOwnershipRatioFraction(
              ownershipRatio.ratioNumerator,
              ownershipRatio.ratioDenominator,
            )
          : formatOwnershipRatioPercentage(ownershipRatio?.manualRatio),
    },
    {
      prop: 'ratioNumerator',
      label: isBefore2025Source
        ? t('ownership-ratio-numerator-before-2025')
        : t('ownership-ratio-numerator'),
      converter: (_, ownershipRatio) =>
        ownershipRatio?.operationType === 'auto'
          ? formatOwnershipRatioPartsOfFraction(ownershipRatio.ratioNumerator)
          : '',
    },
    {
      prop: 'ratioDenominator',
      label: isBefore2025Source
        ? t('ownership-ratio-denominator-before-2025')
        : t('ownership-ratio-denominator'),
      converter: (_, ownershipRatio) =>
        ownershipRatio?.operationType === 'auto'
          ? formatOwnershipRatioPartsOfFraction(ownershipRatio.ratioDenominator)
          : '',
    },
  ];

  const ownershipRatioLegendMap: Array<DiffViewConfigType<OwnershipRatio>> = [
    ...(!isBefore2025Source
      ? [
          {
            prop: 'basis.contentsOfHeldFundAmount' as const,
            label: t('contents-of-held-fund-amount-of-ownership-ratio'),
            isLongField: true,
          },
          {
            prop: 'basis.contentsOfFundProjectCosts' as const,
            label: t('contents-of-fund-project-costs-of-ownership-ratio'),
            isLongField: true,
          },
        ]
      : []),
    ...(isBefore2025Target
      ? [
          {
            prop: 'basis.calculationFormula' as const,
            label: t('basis-calculation-formula'),
            isLongField: true,
          },
          {
            prop: 'basis.contents' as const,
            label: t('contents-of-each-term'),
            isLongField: true,
          },
          {
            prop: 'conceptOfProjectProspect.calculationFormula' as const,
            label: t('concept-of-basis-calculation-formula'),
            isLongField: true,
          },
        ]
      : []),
    {
      prop: 'conceptOfProjectProspect.contents',
      label: isBefore2025Source
        ? t('concept-of-contents-of-each-term-before-2025')
        : t('concept-of-contents-of-each-term'),
      isLongField: true,
    },
    {
      prop: 'conceptOfProjectProspect.basisForCalculationOfIndicators',
      label: t('basis-for-estimating-indicators-for-project-prospect'),
      isLongField: true,
    },
    {
      prop: 'conceptOfProjectProspect.moreRecentResults',
      label: t('indicators-for-project-prospect-and-recent-performance'),
      isLongField: true,
    },
  ];

  const lowUsedFundInvestigationLegendMap: Array<
    DiffViewConfigType<PartialInspectionEvaluation>
  > = [
    {
      prop: 'lowUsedFundInvestigation.hasCeasedOperations',
      label: t('has-ceased-operations'),
      converter: (value) =>
        typeof value === 'boolean'
          ? value
            ? ExistenceOrAbsenceLabelModifier.true
            : ExistenceOrAbsenceLabelModifier.false
          : '',
      isLongField: true,
    },
    {
      prop: 'lowUsedFundInvestigation.hasNoResultRecent',
      label: t('has-no-operation-fund-detected'),
      converter: (value) =>
        typeof value === 'boolean'
          ? value
            ? ExistenceOrAbsenceLabelModifier.true
            : ExistenceOrAbsenceLabelModifier.false
          : '',
      isLongField: true,
    },
    {
      prop: 'lowUsedFundInvestigation.hasFundThatLostPurpose',
      label: t('has-fund-that-lost-purpose'),
      converter: (value) =>
        typeof value === 'boolean'
          ? value
            ? ExistenceOrAbsenceLabelModifier.true
            : ExistenceOrAbsenceLabelModifier.false
          : '',
      isLongField: true,
    },
    {
      prop: 'lowUsedFundInvestigation.hasExceedOneSignificantly',
      label: t('has-exceed-1-significantly'),
      converter: (value) =>
        typeof value === 'boolean'
          ? value
            ? ExistenceOrAbsenceLabelModifier.true
            : ExistenceOrAbsenceLabelModifier.false
          : '',
      isLongField: true,
    },
    {
      prop: 'lowUsedFundInvestigation.hasDeemedUnlikelyToBeUsed',
      label: t('has-deemed-unlikely-to-be-used'),
      converter: (value) =>
        typeof value === 'boolean'
          ? value
            ? ExistenceOrAbsenceLabelModifier.true
            : ExistenceOrAbsenceLabelModifier.false
          : '',
      isLongField: true,
    },
    {
      prop: 'lowUsedFundInvestigation.reasonsForBeingFalseWhileExceedingOne',
      label: t('reason-for-selecting-absense-when-ownership-ratio-exceeds-1'),
      isLongField: true,
    },
    {
      prop: 'lowUsedFundInvestigation.resultsOfInvestigations',
      label: t('consideration-where-the-fund-are-unlikely-to-be-used'),
      isLongField: true,
    },
    {
      prop: 'lowUsedFundInvestigation.reasonsForLeaving',
      label: t('reason-for-leaving-the-fund-that-are-unlikely-to-be-used'),
      isLongField: true,
    },
  ];

  const appropriatenessInspectionLegendMap: Array<
    DiffViewConfigType<PartialInspectionEvaluation>
  > = isBefore2025Target
    ? [
        {
          prop: 'appropriatenessInspection.reasonsWhyBulkDeliveringWasNecessary',
          label: t('reason-why-bulk-delivering-was-necessary'),
          isLongField: true,
        },
        {
          prop: 'appropriatenessInspection.approachInDeterminingTheTimingAndAmountOfAdditions',
          label: t(
            'approach-in-determining-the-timing-and-amount-of-additions',
          ),
          isLongField: true,
        },
      ]
    : [];

  const implementationStatusOfInspectionLegendMap: Array<
    DiffViewConfigType<PartialInspectionEvaluation>
  > = isBefore2025Target
    ? [
        {
          prop: 'implementationStatusOfInspection',
          label: t('implementation-status-of-inspection'),
          isLongField: true,
        },
      ]
    : [];

  const eligibilityInspectionLegendMap: Array<
    DiffViewConfigType<PartialInspectionEvaluation>
  > = isBefore2025Target
    ? [
        {
          prop: 'eligibilityInspection.methodAndReasonsForSelection',
          label: t(
            'eligibility-inspection-method-and-reasons-for-selection-before-2025',
          ),
          isLongField: true,
        },
        {
          prop: 'eligibilityInspection.results',
          label: t('eligibility-inspection-results-before-2025'),
          isLongField: true,
        },
      ]
    : [];

  const inspectionAndImprovementByAdministrativeJurisdictionLegendMap: Array<
    DiffViewConfigType<PartialInspectionEvaluation>
  > = [
    {
      prop: 'inspectionAndImprovementByAdministrativeJurisdiction.inspectionResult',
      label: t('inspection-result'),
      isLongField: true,
    },
    {
      prop: 'inspectionAndImprovementByAdministrativeJurisdiction.evaluation',
      label: t('evaluation'),
      isLongField: true,
    },
    {
      prop: 'inspectionAndImprovementByAdministrativeJurisdiction.directionOfImprovement',
      label: t('direction-of-improvement'),
      isLongField: true,
    },
  ];

  const inspectionByOtherThanFundJurisdictionLegendMap: Array<
    DiffViewConfigType<PartialInspectionEvaluation>
  > = [
    {
      prop: 'findingsOfExternalExperts',
      label: t('findings-of-external-experts'),
      isLongField: true,
    },
    {
      prop: 'processLeadingToTheFindingsOfReviewPromotionTeam',
      label: t('process-leading-to-the-findings-of-review-promotion-team'),
      isLongField: true,
    },
    {
      prop: 'improvementPointBasedOnTheFindings',
      label: t('improvement-point-based-on-the-findings'),
      isLongField: true,
    },
    ...(isBefore2025Target
      ? [
          {
            prop: 'overviewOfReviewsCarriedOutInThePast' as const,
            label: t('overview-of-reviews-carried-out-in-the-past'),
            isLongField: true,
          },
        ]
      : []),
  ];

  return (
    <Card>
      <Row gutter={[8, 0]}>
        {isBefore2025Target ? (
          <DiffViewerTitleCols
            label={t('self-inspection-by-fund-jurisdiction')}
          />
        ) : (
          <DiffViewerTitleCols label={t('ownership-ratio')} />
        )}
        {selfInspectionByFundJurisdictionLegendMap.map(
          ({ prop, label, converter, isLongField }) => (
            <DiffViewerContentCols<Exclude<OwnershipRatio, undefined | null>>
              key={prop}
              legend={label}
              prop={prop}
              textConverter={converter}
              isLongField={isLongField}
              showLegendAnytime={{ source: true, target: true }}
              source={sourceInspectionEvaluation.ownershipRatio ?? undefined}
              target={targetInspectionEvaluation.ownershipRatio ?? undefined}
            />
          ),
        )}
        {isBefore2025Target && (
          <DiffViewerTitleCols label={t('ownership-ratio')} />
        )}
        {ownershipRatioLegendMap.map(
          ({ prop, label, converter, isLongField }) => (
            <DiffViewerContentCols<Exclude<OwnershipRatio, undefined | null>>
              key={prop}
              legend={label}
              prop={prop}
              textConverter={converter}
              isLongField={isLongField}
              showLegendAnytime={{ source: true, target: true }}
              source={sourceInspectionEvaluation.ownershipRatio ?? undefined}
              target={targetInspectionEvaluation.ownershipRatio ?? undefined}
            />
          ),
        )}
        <DiffViewerTitleCols label={t('low-used-fund-investigation')} />
        {lowUsedFundInvestigationLegendMap.map(
          ({ prop, label, converter, isLongField }) => (
            <DiffViewerContentCols<PartialInspectionEvaluation>
              key={prop}
              legend={label}
              prop={prop}
              textConverter={converter}
              isLongField={isLongField}
              source={sourceInspectionEvaluation}
              target={targetInspectionEvaluation}
            />
          ),
        )}
        {isBefore2025Target && (
          <>
            <DiffViewerTitleCols label={t('appropriateness-inspection')} />
            {appropriatenessInspectionLegendMap.map(
              ({ prop, label, converter, isLongField }) => (
                <DiffViewerContentCols<PartialInspectionEvaluation>
                  key={prop}
                  legend={label}
                  prop={prop}
                  textConverter={converter}
                  isLongField={isLongField}
                  source={sourceInspectionEvaluation}
                  target={targetInspectionEvaluation}
                />
              ),
            )}
            <DiffViewerTitleCols
              label={t('implementation-status-of-inspection')}
            />
            {implementationStatusOfInspectionLegendMap.map(
              ({ prop, label, converter, isLongField }) => (
                <DiffViewerContentCols<PartialInspectionEvaluation>
                  key={prop}
                  legend={label}
                  prop={prop}
                  textConverter={converter}
                  isLongField={isLongField}
                  source={sourceInspectionEvaluation}
                  target={targetInspectionEvaluation}
                />
              ),
            )}
            <DiffViewerTitleCols label={t('eligibility-inspection')} />
            {eligibilityInspectionLegendMap.map(
              ({ prop, label, converter, isLongField }) => (
                <DiffViewerContentCols<PartialInspectionEvaluation>
                  key={prop}
                  legend={label}
                  prop={prop}
                  textConverter={converter}
                  isLongField={isLongField}
                  source={sourceInspectionEvaluation}
                  target={targetInspectionEvaluation}
                />
              ),
            )}
          </>
        )}
        <DiffViewerTitleCols
          label={t('inspection-and-improvement-by-fund-jurisdiction')}
        />
        {inspectionAndImprovementByAdministrativeJurisdictionLegendMap.map(
          ({ prop, label, converter, isLongField }) => (
            <DiffViewerContentCols<PartialInspectionEvaluation>
              key={prop}
              legend={label}
              prop={prop}
              textConverter={converter}
              isLongField={isLongField}
              source={sourceInspectionEvaluation}
              target={targetInspectionEvaluation}
            />
          ),
        )}
        <DiffViewerTitleCols
          label={t('inspection-by-other-than-fund-jurisdiction')}
        />
        {inspectionByOtherThanFundJurisdictionLegendMap.map(
          ({ prop, label, converter, isLongField }) => (
            <DiffViewerContentCols<PartialInspectionEvaluation>
              key={prop}
              legend={label}
              prop={prop}
              textConverter={converter}
              isLongField={isLongField}
              source={sourceInspectionEvaluation}
              target={targetInspectionEvaluation}
            />
          ),
        )}
      </Row>
    </Card>
  );
};
